/**
 * -----------------------------------------------------------------------------
 * Vehicle Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the vehicle details.
 */

import { FormikProps } from "formik";
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GroupBase, OptionsOrGroups } from "react-select";
import { toast } from "react-toastify";
import ColorPicker from "rsuite-color-picker";
import {
  useDeleteVehicleZipTieMutation,
  useLazyOrganizationDriversQuery,
  useLazyOrganizationGroupsQuery,
  useLazyVehicleDecodeVinQuery,
  useOrganizationDriversQuery,
  useOrganizationGroupsQuery,
  usePatchVehicleZipTieMutation,
  usePostVehicleZipTieMutation,
  useVehicleZipTiesQuery,
} from "../../../api/network/adminApiServices";
import { useLazyGetDeviceListingQuery } from "../../../api/network/camerasAdminVMApiService";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { DeviceListingResponse } from "../../../api/types/Camera";
import { TLegacyDriver } from "../../../api/types/Driver";
import { OrgGHGroup, TLegacyGroup } from "../../../api/types/Group";
import svgIcons from "../../../assets/svg/svgIcons";
import { AdminFormFileInput } from "../../../components/admin/fileFields";
import FormActionButtons from "../../../components/admin/formActionButtons";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  PillItem,
  PseudoSelect,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import ModalDeleteImageConfirmation from "../../../components/admin/modalDeleteImageConfirmation";
import HierarchyGroupSelector from "../../../components/common/hierarchyGroupSelector";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useAccountTimezone } from "../../../hooks/timezone";
import { asyncDebounce } from "../../../utils/common";
import {
  formattedDateTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../utils/dateTime";
import { getRoundedOffValue } from "../../../utils/distance";
import { driverDisplayName } from "../../../utils/driver";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import {
  ASSET_ICON_TYPE_OPTIONS,
  ASSET_ICON_TYPES,
  DISTANCE_UNIT_VALUES,
  DISTANCE_UNITS,
  DISTANCE_UNITS_OPTIONS,
  ECONOMY_UNITS,
  ECONOMY_UNITS_OPTIONS,
  ECONOMY_UNITS_VALUES,
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
  STANDARD_ICONS_LIST,
  VOLUME_UNIT_VALUES,
  VOLUME_UNITS,
  VOLUME_UNITS_OPTIONS,
} from "../account/constants";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import {
  ASSET_TYPE_OPTIONS,
  CAMERA_TYPES,
  EQUIPMENT_STATUS_OPTIONS,
  FUEL_TYPE_OPTIONS,
  IGNITION_INPUT_OPTIONS,
  RECORDER_ON_OPTIONS,
  RECORDER_TYPE_OPTIONS_SURFSIGHT,
  RECORDER_TYPE_OPTIONS_VISIONMAX,
  VEHICLE_CLASS_OPTIONS,
} from "./constants";
import {
  vehicleFormInitialValues,
  vehicleUploadFormInitialValues,
} from "./validation";
import VehicleStandardIconSelector from "./VehicleStandardIconSelector";
import { VehicleZipTieDetails } from "../../../api/types/Vehicle";

interface VehicleDetailFormProps {
  values: typeof vehicleFormInitialValues;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formik?: FormikProps<typeof vehicleFormInitialValues>;
  uploadFormik?: FormikProps<typeof vehicleUploadFormInitialValues>;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
  setDecodeVinData?: Dispatch<
    SetStateAction<{
      make: string;
      model: string;
      modelyear: string;
    }>
  >;
  setFieldToReset?: Dispatch<SetStateAction<string>>;
}

export const VehicleDetailForm: FC<VehicleDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formik,
  uploadFormik,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  setDecodeVinData,
  setFieldToReset,
}) => {
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgIsBorderCrossing = useLoggedInUserData("isbordercrossing");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const thisOrgAssetImageOnMap = useLoggedInUserData("assetimageonmap");
  const showDriver = thisOrgDriverAllowed === "1";

  const { t } = useTranslation("translation", {
    keyPrefix: "admins.vehicles.detailsPage.form",
  });

  const {
    values: uploadFormValues,
    errors: uploadFormErrors,
    handleSubmit: uploadFormHandleSubmit,
    setFieldValue: uploadFormSetFieldValue,
  } = uploadFormik || {};
  const [fileName, setFileName] = useState<string | null>(null);

  // speed units
  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  // distance unit
  const orgDistanceUnit = useLoggedInUserData(
    "distanceUnits"
  ) as DISTANCE_UNIT_VALUES;
  const distanceUnit: DISTANCE_UNITS =
    DISTANCE_UNITS_OPTIONS.find((option) => option.value === orgDistanceUnit)
      ?.unit || DISTANCE_UNITS_OPTIONS[0].unit;

  // volume units
  const orgVolumeUnit = useLoggedInUserData(
    "volumeUnits"
  ) as VOLUME_UNIT_VALUES;
  const volumeUnit: VOLUME_UNITS =
    VOLUME_UNITS_OPTIONS.find((option) => option.value === orgVolumeUnit)
      ?.unit || VOLUME_UNITS_OPTIONS[0].unit;

  // economy units
  const orgEconomyUnit = useLoggedInUserData(
    "economyUnits"
  ) as ECONOMY_UNITS_VALUES;
  const economyUnit: ECONOMY_UNITS =
    ECONOMY_UNITS_OPTIONS.find((option) => option.value === orgEconomyUnit)
      ?.unit || ECONOMY_UNITS_OPTIONS[0].unit;

  const {
    data: dataInitialDriversList,
    isFetching: isFetchingInitialDriversList,
  } = useOrganizationDriversQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
  });
  const { getUserACLAccessLevel } = useACL();
  const [orgDriversQuery, {}] = useLazyOrganizationDriversQuery();
  const [dropdownSearchResults, setDropdownSearchResults] = useState<
    TLegacyDriver[]
  >([]);

  const [showIconPicker, setShowIconPicker] = useState(false);
  const loadOptionsHandlerDriver = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: driversQueryResponse } = await orgDriversQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 3, // Setting default to 3 as 2 is set from additional props
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.DRIVER_DROPDOWN.join(","), // search by driverid only
    });

    if (!driversQueryResponse || !driversQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 3,
        },
      };
    }
    const { results } = driversQueryResponse as {
      results: TLegacyDriver[];
    };
    setDropdownSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyDriver[])).map(
            (item: TLegacyDriver, index: number) =>
              ({
                value: `${item.driverid}`,
                label: driverDisplayName(item),
              }) as TSelectboxOption
          )
        : [],
      hasMore: driversQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 3,
      },
    };
  };
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const isSumManuallyEdited = useRef(false);
  useEffect(() => {
    if (isSumManuallyEdited.current) {
      isSumManuallyEdited.current = false;
      return;
    }
    const reported =
      parseFloat(values.reported_odometer?.toString() || "0") || 0;
    const offset = parseFloat(values.odometer_offset) || 0;

    const sum = reported + offset;

    // Update the odometer_sum field
    formikSetValue("odometer_sum", sum);
  }, [values.reported_odometer, values.odometer_offset]);

  const handleOdometerSumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    formikSetValue("odometer_sum", raw); // store as string while typing
    const sum = parseFloat(raw) || 0;
    const reported =
      parseFloat(values.reported_odometer?.toString() || "0") || 0;
    formikSetValue("odometer_offset", parseFloat((sum - reported).toFixed(2)));
  };

  const isHoursSumManuallyEdited = useRef(false);
  useEffect(() => {
    if (isHoursSumManuallyEdited.current) {
      isHoursSumManuallyEdited.current = false;
      return;
    }
    const reported = parseFloat(values.reported_hours?.toString() || "0") || 0;
    const offset = parseFloat(values.engine_hours_offset) || 0;

    const sum = reported + offset;

    // Update the hours_sum field
    formikSetValue("hours_sum", sum);
  }, [values.reported_hours, values.engine_hours_offset]);

  const handleHoursSumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    formikSetValue("hours_sum", raw);
    const sum = parseFloat(raw) || 0;
    const reported = parseFloat(values.reported_hours?.toString() || "0") || 0;
    formikSetValue(
      "engine_hours_offset",
      parseFloat((sum - reported).toFixed(2))
    );
  };
  const debouncedLoadOptionsHandlerDriver = asyncDebounce(
    loadOptionsHandlerDriver,
    APP_CONFIG.DEBOUNCE.SEARCH
  );
  const [isFieldLoading, setIsFieldLoading] = useState<string>();
  const [
    vehicleDecodeVinApiTrigger,
    { isFetching: isFetchingDecodeVinData, isLoading },
  ] = useLazyVehicleDecodeVinQuery();
  const handleDecodeVinButton = (vinName: string) => {
    setIsFieldLoading(vinName);
    vehicleDecodeVinApiTrigger({
      asset_vin:
        vinName == "equipment_type"
          ? String(values?.equipment_type)
          : String(values.vin),
    })
      .unwrap()
      .then((response) => {
        if (response) {
          const data = response.Results.filter((item) =>
            ["Make", "Model", "Model Year"].includes(item?.Variable)
          )?.map((item) => ({
            [item?.Variable]: item?.Value,
          }));
          const decodedObject = Object.assign({}, ...data);
          setDecodeVinData?.({
            make: decodedObject?.["Make"],
            model: decodedObject?.["Model"],
            modelyear: decodedObject?.["Model Year"],
          });
          // Open the confirmation modal only after decodeVinData is set,
          // so the modal renders with the decoded values immediately.
          dispatch(
            setModalsData({ ...modalsState, showModalAssetDecodeVIN: true })
          );
        }
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.send_failed")];
        toast.error(errors?.join(" "));
      })
      .finally(() => {
        setIsFieldLoading(undefined);
      });
  };

  // reset image selection field on image change (API)
  useEffect(() => {
    if (!values?.asset_image_url) {
      uploadFormSetFieldValue?.("image", null);
    }
  }, [values?.asset_image_url]);

  // retrieve account timezone
  const { tzForDateFns } = useAccountTimezone();

  const selectedIconPath = useMemo(() => {
    if (!values.standard_icon_key) return null;
    return (
      STANDARD_ICONS_LIST.find((item) => item.key === values.standard_icon_key)
        ?.icon ||
      STANDARD_ICONS_LIST.find((item) => item.name === values.standard_icon_key)
        ?.icon ||
      null
    );
  }, [values.standard_icon_key]);

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {thisOrgAssetImageOnMap === "1" && (
        <div className="col-span-12 flex items-start gap-10 bg-gray-100 pt-4 pb-2">
          {/* 1. Circle & Upload Section */}
          {values.icon_type !== ASSET_ICON_TYPES.STATUS_BASED && (
            <div className="flex flex-col items-center min-w-[144px]">
              <div
                className="flex items-center justify-center bg-white bg-contain bg-origin-content bg-no-repeat bg-center size-36 rounded-full shadow-sm transition-all p-3"
                style={{
                  border: `4px solid ${values.map_border_color || "#d1d5db"}`,

                  backgroundImage:
                    values.icon_type === ASSET_ICON_TYPES.CUSTOM_IMAGE &&
                    !!values.asset_image_url
                      ? `url(${values.asset_image_url})`
                      : "none",
                }}
              >
                {values.icon_type === ASSET_ICON_TYPES.STANDARD_ICON &&
                  (selectedIconPath ? (
                    <img
                      src={selectedIconPath}
                      alt="Selected"
                      className="size-20 transition-all"
                    />
                  ) : (
                    <span className="text-[10px] text-gray-400 font-bold">
                      {t("no_icon")}
                    </span>
                  ))}

                {values.icon_type === ASSET_ICON_TYPES.CUSTOM_IMAGE &&
                  !values.asset_image_url && (
                    <span className="text-xs font-bold text-gray-400">
                      {t("no_image")}
                    </span>
                  )}
              </div>

              {/* For Custom Icons */}
              {values.icon_type === ASSET_ICON_TYPES.CUSTOM_IMAGE && (
                <form
                  className={`flex gap-2 mt-4 ${loadingData || !userCanEdit ? "pointer-events-none" : ""}`}
                  onSubmit={uploadFormHandleSubmit}
                >
                  <AdminFormFileInput
                    label={
                      <span className="flex justify-center items-center gap-2 whitespace-nowrap">
                        <img
                          className={`${loadingData ? "size-6" : "size-4"}${loadingData || !userCanEdit ? " grayscale opacity-50" : ""}`}
                          src={
                            loadingData
                              ? svgIcons.LoadingAnimation
                              : svgIcons.Upload
                          }
                          alt={t("upload")}
                        />
                        {t("upload")}
                      </span>
                    }
                    id="File"
                    onChange={(file) => {
                      if (loadingData || !userCanEdit || !file) return;

                      const allowedTypes = ["image/png", "image/jpeg"];
                      if (!allowedTypes.includes(file.type)) {
                        toast.error("Only PNG and JPG formats are supported");
                        return;
                      }

                      const reader = new FileReader();
                      reader.onload = (e) => {
                        const base64 = e.target?.result as string;
                        const img = new Image();
                        img.onload = () => {
                          const { width, height } = img;
                          if (width < 32 || height < 32)
                            return toast.error(
                              "Image must be at least 32×32 px"
                            );
                          if (width > 256 || height > 256)
                            return toast.error(
                              "Image must not exceed 256×256 px"
                            );
                          setFileName(file.name);
                          uploadFormSetFieldValue?.("image", file);
                          formikSetValue("asset_image_url", base64);
                        };
                        img.src = base64;
                      };
                      reader.readAsDataURL(file);
                    }}
                    fileName={fileName}
                    value={uploadFormValues?.image}
                    error={uploadFormErrors?.image}
                    disabled={loadingData || !userCanEdit}
                    customWrapperClass="relative"
                    allowedFields={
                      APP_CONFIG.DOCUMENT_UPLOAD.ALLOWED_IMAGE_FILE_EXT
                    }
                    buttonSkin={true}
                  />
                  {(!!values.asset_image_url || !!uploadFormValues?.image) &&
                    userCanEdit && (
                      <AdminFormFieldSubmit
                        type="button"
                        variant="danger"
                        buttonDimensionClass="h-10 w-full px-2.5"
                        label={
                          <img
                            className="size-[14px]"
                            src={svgIcons.CrossRedIcon}
                            alt="Remove"
                          />
                        }
                        disabled={loadingData || !userCanEdit}
                        onClick={() =>
                          dispatch(
                            setModalsData({
                              ...modalsState,
                              showDeleteImageConfirmation: true,
                            })
                          )
                        }
                      />
                    )}
                </form>
              )}
            </div>
          )}
          <div className="flex-1 flex flex-col gap-4">
            {/* Icon Dropdown */}
            <div className="relative">
              <AdminFormFieldDropdown
                loadingData={loadingData}
                label={t("icon_type")}
                id="icon_type"
                name="icon_type"
                disabled={!userCanEdit}
                value={values.icon_type || ASSET_ICON_TYPES.STATUS_BASED}
                options={ASSET_ICON_TYPE_OPTIONS}
                onChange={(e) => {
                  setShowIconPicker(false);
                  formikSetValue("icon_type", e?.value);
                  if (e?.value === ASSET_ICON_TYPES.CUSTOM_IMAGE) {
                    formikSetValue("asset_image_url", "");
                  }
                  setShowIconPicker(
                    e?.value === ASSET_ICON_TYPES.STANDARD_ICON
                  );
                }}
                onBlur={handleBlur}
              />
              {showIconPicker &&
                values.icon_type === ASSET_ICON_TYPES.STANDARD_ICON && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowIconPicker(false)}
                    />
                    <div className="absolute z-50 w-full mt-1">
                      <VehicleStandardIconSelector
                        selectedIcon={values.standard_icon_key}
                        onSelect={(iconKey) => {
                          formikSetValue("standard_icon_key", iconKey);
                          setShowIconPicker(false);
                        }}
                      />
                    </div>
                  </>
                )}
            </div>

            {/* Color Palette */}
            <div className="flex flex-col">
              <label className="block text-sm font-display font-semibold text-field-label-valid">
                Icon Border Color
              </label>
              <div className="mt-1 h-11 flex w-36">
                <ColorPicker
                  disabled={!userCanEdit}
                  defaultValue="#ffffffaf"
                  value={values.map_border_color}
                  onChange={(color: any) => {
                    const hexColor =
                      typeof color === "string" ? color : color?.hex || color;
                    formikSetValue("map_border_color", hexColor);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <ModalDeleteImageConfirmation
        handleDeleteAdmin={() => {
          setFileName(null);
          uploadFormSetFieldValue?.("image", null);
          formikSetValue("asset_image_url", "");
          // uploadFormik?.submitForm();
        }}
      />

      <AdminFormFieldInput
        label={t("vehicle_id")}
        type="text"
        id="vehicle_id"
        name="vehicle_id"
        value={values.vehicle_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.vehicle_id}
        touched={touched.vehicle_id}
        disabled={!userCanEdit}
        detailsFormField={true}
        readOnly={true}
        customWrapperClass="col-span-6"
      />

      <AdminFormFieldInput
        label={t("creation_date")}
        type="datetime-local"
        id="creation_date"
        name="creation_date"
        value={formattedDateTime(values.creation_date, "yyyy-MM-dd HH:mm")}
        onChange={(e) => {
          formikSetValue("creation_date", e.target.value);
        }}
        onBlur={(e) => {
          formikSetTouched("creation_date", true);
          handleBlur(e);
        }}
        error={errors.creation_date}
        touched={touched.creation_date}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />

      {/* {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_SERVER_ID) >= ROLE_ACCESS_LEVELS.READ && ( */}
      <AdminFormFieldInput
        label={t("server_id")}
        type="text"
        id="server_id"
        name="server_id"
        value={values.server_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.server_id}
        touched={touched.server_id}
        disabled={!userCanEdit} // getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_SERVER_ID)>= ROLE_ACCESS_LEVELS.READ ||
        readOnly={true}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
      {/* )} */}

      {/* {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FIRMWARE) >= ROLE_ACCESS_LEVELS.READ && ( */}
      <AdminFormFieldInput
        label={t("firmware_version")}
        type="text"
        id="firmware_version"
        name="firmware_version"
        value={values.firmware_version}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.firmware_version}
        touched={touched.firmware_version}
        disabled={!userCanEdit} // getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FIRMWARE)>= ROLE_ACCESS_LEVELS.READ ||
        readOnly={true}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
      {/* )} */}

      <AdminFormFieldInput
        label={t("previous_unique_id")}
        type="text"
        id="previous_unique_id"
        name="previous_unique_id"
        value={values.previous_unique_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.previous_unique_id}
        touched={touched.previous_unique_id}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("unique_id_last_changed")}
        type="date-picker"
        id="unique_id_last_changed"
        name="unique_id_last_changed"
        value={formattedDateTime(values.unique_id_last_changed, "yyyy-MM-dd")}
        onDatepickerChange={(_, value) => {
          formikSetValue("unique_id_last_changed", value);
        }}
        onBlur={(e) => {
          formikSetTouched("unique_id_last_changed", true);
          handleBlur(e);
        }}
        error={errors.unique_id_last_changed}
        touched={touched.unique_id_last_changed}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
        size="lg"
      />
      {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_UNIQUE_ID) >=
        ROLE_ACCESS_LEVELS.READ && (
        <div className="col-span-6 grid">
          <AdminFormFieldInput
            customWrapperClass="col-span-6 lg:mr-2"
            label={t("unique_id")}
            type="text"
            id="unique_id"
            name="unique_id"
            value={values.unique_id}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.unique_id}
            touched={touched.unique_id}
            disabled={
              getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_UNIQUE_ID) <=
                ROLE_ACCESS_LEVELS.READ || !userCanEdit
            }
            detailsFormField={true}
          />
        </div>
      )}
      <AdminFormFieldInput
        label={t("department_id")}
        type="text"
        id="department_id"
        name="department_id"
        value={values.groupid}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.department_id}
        touched={touched.department_id}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />
      <AdminFormFieldInput
        label={t("last_timestamp")}
        type="text"
        id="last_timestamp"
        name="last_timestamp"
        value={
          !!values?.lasteventtimestamp && !!tzForDateFns
            ? formattedDateTimeBasedOnTimezone(
                values?.lasteventtimestamp * 1000,
                tzForDateFns
              )
            : "-"
        }
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.lasteventtimestamp}
        touched={touched.lasteventtimestamp}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />
      <div className="col-span-12 py-4">
        <hr />
      </div>
      <div className="flex flex-1 items-center justify-between gap-2 col-span-6">
        <AdminFormFieldInput
          label={t("reported_odometer")}
          type="text"
          id="reported_odometer"
          name="reported_odometer"
          value={values.reported_odometer}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.reported_odometer}
          touched={touched.reported_odometer}
          disabled={!userCanEdit}
          readOnly={true}
          detailsFormField={true}
          customWrapperClass="flex-1"
          bottomLabel={distanceUnit.toLowerCase()}
        />
        {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ODOMETER_OFFSET) >=
          ROLE_ACCESS_LEVELS.WRITE && (
          <AdminFormFieldSubmit
            label={t("reset")}
            buttonDimensionClass="h-11 mt-1 w-full px-4"
            type="button"
            variant="secondary"
            onClick={() => {
              setFieldToReset?.("reported_odometer");
              dispatch(
                setModalsData({ ...modalsState, showResetConfirmation: true })
              );
            }}
            disabled={
              !userCanEdit ||
              getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ODOMETER_OFFSET) <
                ROLE_ACCESS_LEVELS.WRITE
            }
          />
        )}
      </div>

      <div className="flex flex-1 items-center justify-between gap-2 col-span-6  ">
        <AdminFormFieldInput
          label={t("reported_hours")}
          type="text"
          id="reported_hours"
          name="reported_hours"
          value={getRoundedOffValue(values.reported_hours)}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.reported_hours}
          touched={touched.reported_hours}
          disabled={!userCanEdit}
          readOnly={true}
          bottomLabel="Hours"
          detailsFormField={true}
          customWrapperClass="flex-1"
        />
        {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ENG_HOURS_OFFSET) >=
          ROLE_ACCESS_LEVELS.WRITE && (
          <AdminFormFieldSubmit
            label={t("reset")}
            buttonDimensionClass="h-11 mt-1 w-full px-4 "
            type="button"
            variant="secondary"
            onClick={() => {
              setFieldToReset?.("reported_hours");
              dispatch(
                setModalsData({ ...modalsState, showResetConfirmation: true })
              );
            }}
            disabled={
              !userCanEdit ||
              getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ENG_HOURS_OFFSET) <
                ROLE_ACCESS_LEVELS.WRITE
            }
          />
        )}
      </div>
      <div className="flex flex-1 items-center justify-between gap-2 col-span-6">
        <AdminFormFieldInput
          label={t("odometer_offset")}
          type="text"
          id="odometer_offset"
          name="odometer_offset"
          placeholder={parseFloat(values.odometer_offset) == 0 ? "0" : ""}
          value={values.odometer_offset !== "0" ? values.odometer_offset : ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.odometer_offset}
          touched={touched.odometer_offset}
          disabled={
            !userCanEdit ||
            getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ODOMETER_OFFSET) <
              ROLE_ACCESS_LEVELS.WRITE
          }
          detailsFormField={true}
          customWrapperClass="flex-1"
          bottomLabel={distanceUnit.toLowerCase()}
        />
        {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ODOMETER_OFFSET) >=
          ROLE_ACCESS_LEVELS.WRITE && (
          <AdminFormFieldSubmit
            label={t("reset")}
            buttonDimensionClass="h-11 mt-1 w-full px-4"
            type="button"
            variant="secondary"
            onClick={() => {
              setFieldToReset?.("odometer_offset");
              dispatch(
                setModalsData({ ...modalsState, showResetConfirmation: true })
              );
            }}
            disabled={
              !userCanEdit ||
              getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ODOMETER_OFFSET) <
                ROLE_ACCESS_LEVELS.WRITE
            }
          />
        )}
      </div>
      <div className="flex flex-1 items-center justify-between gap-2 col-span-6">
        <AdminFormFieldInput
          label={t("engine_hours_offset")}
          type="text"
          id="engine_hours_offset"
          name="engine_hours_offset"
          placeholder={parseFloat(values.engine_hours_offset) == 0 ? "0" : ""}
          value={
            values.engine_hours_offset !== "0" ? values.engine_hours_offset : ""
          }
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.engine_hours_offset}
          touched={touched.engine_hours_offset}
          disabled={
            !userCanEdit ||
            getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ENG_HOURS_OFFSET) <
              ROLE_ACCESS_LEVELS.WRITE
          }
          detailsFormField={true}
          bottomLabel="Hours"
          customWrapperClass="flex-1"
        />
        {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ENG_HOURS_OFFSET) >=
          ROLE_ACCESS_LEVELS.WRITE && (
          <AdminFormFieldSubmit
            label={t("reset")}
            buttonDimensionClass="h-11 mt-1 w-full px-4"
            type="button"
            variant="secondary"
            onClick={() => {
              setFieldToReset?.("engine_hours_offset");
              dispatch(
                setModalsData({ ...modalsState, showResetConfirmation: true })
              );
            }}
            disabled={
              !userCanEdit ||
              getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_CLR_ENG_HOURS_OFFSET) <
                ROLE_ACCESS_LEVELS.WRITE
            }
          />
        )}
      </div>
      <AdminFormFieldInput
        label={t("odometer_sum")}
        type="text"
        id="odometer_sum"
        name="odometer_sum"
        value={
          typeof values?.odometer_sum === "number"
            ? values.odometer_sum.toFixed(2)
            : (values?.odometer_sum ?? "")
        }
        onChange={(e) => {
          isSumManuallyEdited.current = true;
          handleOdometerSumChange(e);
        }}
        onBlur={handleBlur}
        error={errors.odometer_sum}
        touched={touched.odometer_sum}
        disabled={!userCanEdit}
        // readOnly={true}
        detailsFormField={true}
        bottomLabel={distanceUnit.toLowerCase()}
      />
      <AdminFormFieldInput
        label={t("hours_sum")}
        type="text"
        id="hours_sum"
        name="hours_sum"
        value={
          typeof values?.hours_sum === "number"
            ? String(getRoundedOffValue(values.hours_sum))
            : (values?.hours_sum ?? "")
        }
        onChange={(e) => {
          isHoursSumManuallyEdited.current = true;
          handleHoursSumChange(e);
        }}
        onBlur={handleBlur}
        error={errors.hours_sum}
        touched={touched.hours_sum}
        disabled={!userCanEdit}
        // readOnly={true}
        detailsFormField={true}
        bottomLabel="Hours"
      />
      <div className="col-span-12 py-4">
        <hr />
      </div>

      <div className="grid grid-cols-12 col-span-12">
        {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_ACTIVE) >=
          ROLE_ACCESS_LEVELS.READ && (
          <AdminFormFieldCheckbox
            customWrapperClass="col-span-6 lg:mr-2"
            label={t("is_active")}
            id="is_active"
            type="checkbox"
            name="is_active"
            checked={!!values.is_active}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.is_active}
            touched={touched.is_active}
            disabled={
              getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_ACTIVE) <=
                ROLE_ACCESS_LEVELS.READ || !userCanEdit
            }
          />
        )}
        <AdminFormFieldCheckbox
          customWrapperClass={`col-span-6${
            getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_ACTIVE) >=
            ROLE_ACCESS_LEVELS.READ
              ? " lg:ml-2"
              : " me-2"
          }`}
          label={t("is_border_crossing")}
          id="is_border_crossing"
          type="checkbox"
          name="is_border_crossing"
          checked={!!values.is_border_crossing}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.is_border_crossing}
          touched={touched.is_border_crossing}
          disabled={!userCanEdit || thisOrgIsBorderCrossing === "0"}
          readOnly={thisOrgIsBorderCrossing === "0"}
        />

        <AdminFormFieldCheckbox
          customWrapperClass="col-span-6 mt-2 lg:mr-2"
          label={t("sparse_event_reporting")}
          id="sparseeventreporting"
          type="checkbox"
          name="sparseeventreporting"
          checked={!!values.sparseeventreporting}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.sparseeventreporting}
          touched={touched.sparseeventreporting}
          disabled={!userCanEdit}
        />
      </div>
      <AdminFormFieldInput
        label={t("vehicle_description")}
        type="text"
        id="vehicle_description"
        name="vehicle_description"
        value={values.vehicle_description}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.vehicle_description}
        touched={touched.vehicle_description}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("short_name")}
        type="text"
        id="short_name"
        name="short_name"
        value={values.short_name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.short_name}
        touched={touched.short_name}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <div className="flex flex-1 items-end justify-between gap-2 col-span-6">
        <AdminFormFieldInput
          label={t("vin")}
          type="text"
          id="vin"
          name="vin"
          value={values.vin}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.vin}
          touched={touched.vin}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="flex-1"
          bottomLabel={
            APP_CONFIG.DEBUG.ALL ? "Field: equipmenttype" : undefined
          }
        />
        {/* Loader */}
        {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_VIN_DECODE) >=
          ROLE_ACCESS_LEVELS.WRITE && (
          <div className="col-span-6">
            {isFieldLoading === "vin" ? (
              <div className="flex justify-center mt-0">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="Loading animation"
                  className="w-12 h-12"
                />
              </div>
            ) : (
              <AdminFormFieldSubmit
                label={t("decode_vin")}
                buttonDimensionClass="h-11 w-full px-4"
                type="button"
                variant="primary"
                onClick={() => {
                  handleDecodeVinButton("vin");
                }}
                disabled={
                  !userCanEdit ||
                  isLoading ||
                  isFetchingDecodeVinData ||
                  !values.vin
                }
              />
            )}
          </div>
        )}
      </div>

      <AdminFormFieldInput
        label={t("vehicle_make")}
        type="text"
        id="vehicle_make"
        name="vehicle_make"
        value={values.vehicle_make}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.vehicle_make}
        touched={touched.vehicle_make}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("vehicle_model")}
        type="text"
        id="vehicle_model"
        name="vehicle_model"
        value={values.vehicle_model}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.vehicle_model}
        touched={touched.vehicle_model}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <AdminFormFieldInput
        label={t("vehicle_year")}
        type="text"
        id="vehicle_year"
        name="vehicle_year"
        value={values.vehicle_year}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.vehicle_year}
        touched={touched.vehicle_year}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("license_plate")}
        type="text"
        id="license_plate"
        name="license_plate"
        value={values.license_plate}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.license_plate}
        touched={touched.license_plate}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("license_expiration")}
        type="date-picker"
        id="license_expiration"
        name="license_expiration"
        value={formattedDateTime(values.license_expiration, "yyyy-MM-dd")}
        onDatepickerChange={(_, value) => {
          formikSetValue(
            "license_expiration",
            formattedDateTime(value, "yyyy-MM-dd")
          );
        }}
        onBlur={(e) => {
          formikSetTouched("license_expiration", true);
          handleBlur(e);
        }}
        error={errors.license_expiration}
        touched={touched.license_expiration}
        disabled={!userCanEdit}
        detailsFormField={true}
        size="lg"
      />
      <div className="flex flex-1 items-end justify-between gap-2 col-span-6">
        <AdminFormFieldInput
          label={t("equipment_type")}
          type="text"
          id="equipment_type"
          name="equipment_type"
          value={values.equipment_type}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.equipment_type}
          touched={touched.equipment_type}
          disabled={!userCanEdit}
          readOnly={true} // updated by OBD via the GPS device
          detailsFormField={true}
          customWrapperClass="flex-1"
          bottomLabel={APP_CONFIG.DEBUG.ALL ? "Field: vehicleid" : undefined}
        />
        {/* Loader */}
        {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_VIN_DECODE) >=
          ROLE_ACCESS_LEVELS.WRITE && (
          <div className="col-span-6">
            {isFieldLoading === "equipment_type" ? (
              <div className="flex justify-center mt-0">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="Loading animation"
                  className="w-12 h-12"
                />
              </div>
            ) : (
              <AdminFormFieldSubmit
                label={t("decode_vin")}
                buttonDimensionClass="h-11 w-full px-4"
                type="button"
                variant="primary"
                onClick={() => handleDecodeVinButton("equipment_type")}
                disabled={
                  !userCanEdit ||
                  isLoading ||
                  isFetchingDecodeVinData ||
                  !values.equipment_type
                }
              />
            )}
          </div>
        )}
      </div>
      {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EQUIP_STATUS) >=
        ROLE_ACCESS_LEVELS.READ && (
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("equipment_status")}
          id="equipment_status"
          name="equipment_status"
          value={values.equipment_status}
          options={EQUIPMENT_STATUS_OPTIONS}
          onChange={(e) => {
            formikSetValue("equipment_status", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("equipment_status", true);
            handleBlur(e);
          }}
          error={errors.equipment_status}
          touched={touched.equipment_status}
          disabled={
            getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EQUIP_STATUS) <=
              ROLE_ACCESS_LEVELS.READ || !userCanEdit
          }
          detailsFormField={true}
        />
      )}

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("asset_type")}
        id="asset_type"
        name="asset_type"
        value={values.asset_type}
        options={ASSET_TYPE_OPTIONS}
        onChange={(e) => {
          formikSetValue("asset_type", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("asset_type", true);
          handleBlur(e);
        }}
        error={errors.asset_type}
        touched={touched.asset_type}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("vehicle_class")}
        id="vehicle_class"
        name="vehicle_class"
        value={values.vehicle_class}
        options={VEHICLE_CLASS_OPTIONS}
        onChange={(e) => {
          formikSetValue("vehicle_class", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("vehicle_class", true);
          handleBlur(e);
        }}
        error={errors.vehicle_class}
        touched={touched.vehicle_class}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      {/* <AdminFormFieldInput
        label={t("imei_number")}
        type="text"
        id="imei_number"
        name="imei_number"
        value={values.imei_number}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.imei_number}
        touched={touched.imei_number}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      {/* <AdminFormFieldInput
        label={t("serial_number")}
        type="text"
        id="serial_number"
        name="serial_number"
        value={values.serial_number}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.serial_number}
        touched={touched.serial_number}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      {/* <AdminFormFieldInput
        label={t("phone_number")}
        type="text"
        id="phone_number"
        name="phone_number"
        value={values.phone_number}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.phone_number}
        touched={touched.phone_number}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      {/* <AdminFormFieldInput
        label={t("email")}
        type="email"
        id="email"
        name="email"
        value={values.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.email}
        touched={touched.email}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      {/* <AdminFormFieldInput
        label={t("group_pushpin_id")}
        type="text"
        id="group_pushpin_id"
        name="group_pushpin_id"
        value={values.group_pushpin_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.group_pushpin_id}
        touched={touched.group_pushpin_id}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      {/* <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("map_route_color")}
        id="map_route_color"
        name="map_route_color"
        value={values.map_route_color}
        options={MAP_ROUTE_COLOR_OPTIONS}
        onChange={(e) => {
          formikSetValue("map_route_color", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("map_route_color", true);
          handleBlur(e);
        }}
        error={errors.map_route_color}
        touched={touched.map_route_color}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("ignition_input")}
        id="ignition_input"
        name="ignition_input"
        value={`${values.ignition_input}`}
        options={IGNITION_INPUT_OPTIONS}
        onChange={(e) => {
          formikSetValue("ignition_input", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("ignition_input", true);
          handleBlur(e);
        }}
        error={errors.ignition_input}
        touched={touched.ignition_input}
        disabled={!userCanEdit}
        // readOnly={true}
        detailsFormField={true}
      />
      <div className="grid grid-cols-12 col-span-12">
        <AdminFormFieldInput
          customWrapperClass="col-span-6 lg:mr-2"
          label={`${t("maximum_speed")} (${speedUnit})`}
          type="text"
          id="maximum_speed"
          name="maximum_speed"
          value={values.maximum_speed}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.maximum_speed}
          touched={touched.maximum_speed}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="grid grid-cols-12 col-span-12">
        {loadingData || isFetchingInitialDriversList ? (
          <PseudoSelect label={t("driver_id")} />
        ) : (
          <AdminFormFieldAsyncLazyDropdown
            customWrapperClass="col-span-6 lg:mr-2"
            loadingData={loadingData}
            label={t("driver_id")}
            id="driver_id"
            name="driver_id"
            value={!!values.driver_id ? values.driver_id : undefined} // passed undefined to let placeholder display if no value is selected
            placeholder={t("driver_id_placeholder")}
            defaultOptions={dataInitialDriversList?.results?.map(
              (item: TLegacyDriver) => ({
                value: `${item.driverid}`,
                label: driverDisplayName(item),
              })
            )}
            defaultOption={
              !!values.driver_id
                ? {
                    value: values.driver_id,
                    label: values.driver_name ?? values.driver_id,
                  }
                : undefined
            } // passed undefined to let placeholder display if no value is selected
            additional={{ page: 2 }}
            isClearable={true}
            HideDropdownIndicator={true}
            loadOptions={debouncedLoadOptionsHandlerDriver}
            noOptionsMessage={() => t("driver_id_no_item")}
            onChange={(e) => {
              if (APP_CONFIG.DEBUG.ALL) console.log("driver_id", e);
              formikSetValue("driver_id", e?.value ?? "");
              const selectedDriver =
                dataInitialDriversList?.results?.find(
                  (item: TLegacyDriver) => item.driverid === e?.value
                ) ??
                dropdownSearchResults.find(
                  (item) => item.driverid === e?.value
                ) ??
                null;
              formikSetValue(
                "driver_name",
                selectedDriver?.displayname ?? selectedDriver?.driverid ?? ""
              );
              formikSetValue(
                "driver_phone_number",
                selectedDriver?.contactphone ?? ""
              );
              setTimeout(() => {
                formik?.validateForm();
              }, 100); // intentional delay to reset validation error when option is selected
            }}
            onBlur={(e) => {
              formikSetTouched("driver_id", true);
              handleBlur(e);
            }}
            error={errors.driver_id}
            touched={touched.driver_id}
            disabled={!userCanEdit}
            detailsFormField={true}
          />
        )}
      </div>

      {showDriver && (
        <AdminFormFieldInput
          label={t("driver_name")}
          type="text"
          id="driver_name"
          name="driver_name"
          value={values.driver_name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.driver_name}
          touched={touched.driver_name}
          disabled={!userCanEdit}
          readOnly={true}
          detailsFormField={true}
        />
      )}

      <AdminFormFieldInput
        label={t("driver_phone_number")}
        type="text"
        id="driver_phone_number"
        name="driver_phone_number"
        value={values.driver_phone_number}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.driver_phone_number}
        touched={touched.driver_phone_number}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("fuel_type")}
        id="fuel_type"
        name="fuel_type"
        value={`${values.fuel_type}`}
        options={FUEL_TYPE_OPTIONS}
        onChange={(e) => {
          formikSetValue("fuel_type", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("fuel_type", true);
          handleBlur(e);
        }}
        error={errors.fuel_type}
        touched={touched.fuel_type}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FUEL_CAPACITY) >=
        ROLE_ACCESS_LEVELS.READ && (
        <AdminFormFieldInput
          label={`${t("fuel_capacity")} (${VOLUME_UNITS_OPTIONS?.find((item) => item.unit === (volumeUnit as string))?.label || volumeUnit})`}
          type="text"
          id="fuel_capacity"
          name="fuel_capacity"
          value={values.fuel_capacity}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.fuel_capacity}
          touched={touched.fuel_capacity}
          disabled={
            getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FUEL_CAPACITY) <=
              ROLE_ACCESS_LEVELS.READ || !userCanEdit
          }
          detailsFormField={true}
        />
      )}

      {getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FUEL_ECONOMY) >=
        ROLE_ACCESS_LEVELS.READ && (
        <AdminFormFieldInput
          label={`${t("fuel_economy")} (${ECONOMY_UNITS_OPTIONS?.find((item) => item.unit === (economyUnit as string))?.label || economyUnit})`}
          type="text"
          id="fuel_economy"
          name="fuel_economy"
          value={values.fuel_economy}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.fuel_economy}
          touched={touched.fuel_economy}
          disabled={
            getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FUEL_ECONOMY) <=
              ROLE_ACCESS_LEVELS.READ || !userCanEdit
          }
          detailsFormField={true}
        />
      )}

      <AdminFormFieldInput
        label={t("fuel_cost")}
        type="text"
        id="fuel_cost"
        name="fuel_cost"
        value={values.fuel_cost}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.fuel_cost}
        touched={touched.fuel_cost}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
    </div>
  );
};

export const VehicleCameraIdDetailForm: FC<VehicleDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.vehicles.detailsPage.form",
  });
  const { getLocalStorageItem } = useLocalStorage();

  const [getDeviceListingAPITrigger, { isLoading: isLoadingCameraDevices }] =
    useLazyGetDeviceListingQuery();

  const enterpriseAccountData = getLocalStorageItem(
    localStorageKeys.ownerOrganization
  );

  const [searchingDropdownValue, setSearchingDropdownValue] =
    useState<boolean>(false);
  const loadOptionsHandlerUser = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    setSearchingDropdownValue(true);
    const { data: deviceQueryResponse } = await getDeviceListingAPITrigger({
      keyword: inputValue || "",
      model: "",
      status: "",
      offset: (additional?.page ?? 1) * APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      // page: additional?.page ?? 1,
    });
    setSearchingDropdownValue(false);

    if (
      !deviceQueryResponse ||
      !deviceQueryResponse?.data ||
      deviceQueryResponse?.data?.length === 0
    ) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: (additional?.page ?? 1) + 1,
        },
      };
    }
    const results = deviceQueryResponse?.data ?? [];
    const options = !!results
      ? (results || ([] as DeviceListingResponse[]))
          .filter((item: DeviceListingResponse) => item.serial_number !== null) // exclude null camera id reciords
          .map(
            (item: DeviceListingResponse, index: number) =>
              ({
                value: `${item.serial_number}`,
                label: `${item.serial_number}`,
              }) as TSelectboxOption
          )
      : [];

    return {
      options: options,
      hasMore: true,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 1,
      },
    };
  };

  const debouncedLoadOptionsHandlerUser = asyncDebounce(
    loadOptionsHandlerUser,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <div className="col-span-6 text-gray-400 italic">
          [Value=={JSON.stringify(values.recorder_type)}==]
          <br />
          [Enterprise==
          {JSON.stringify(enterpriseAccountData?.recordertype)}==]
          <br />
          [CameraID=={JSON.stringify(values.recorder_id)}==]
        </div>
      )}
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("recorder_type")}
        id="recorder_type"
        name="recorder_type"
        // conversion to applicable value is handled in `vehicle-admin-details.tsx`
        // under formik.setValues() call
        // value={
        //   values.recorder_type?.toString() !== ""
        //     ? values.recorder_type?.toString()
        //     : enterpriseAccountData?.recordertype?.toString()
        // }
        // thus here we directly use the value calculated and set in formik.values
        value={values.recorder_type?.toString()}
        options={
          enterpriseAccountData?.recordertype === CAMERA_TYPES.SURFSIGHT
            ? RECORDER_TYPE_OPTIONS_SURFSIGHT
            : RECORDER_TYPE_OPTIONS_VISIONMAX
        }
        onChange={(e) => {
          formikSetValue("recorder_type", e?.value);
          formikSetValue("recorder_id", "");
          // the setTimeout helps in displaying the validation message appropriately
          setTimeout(() => {
            formikSetTouched("recorder_type");
          }, 200);
        }}
        onBlur={(e) => {
          formikSetTouched("recorder_type", true);
          handleBlur(e);
        }}
        error={errors.recorder_type}
        touched={touched.recorder_type}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("recorder_on")}
        id="recorder_on"
        name="recorder_on"
        value={`${values.recorder_on}`}
        options={RECORDER_ON_OPTIONS}
        onChange={(e) => {
          formikSetValue("recorder_on", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("recorder_on", true);
          handleBlur(e);
        }}
        error={errors.recorder_on}
        touched={touched.recorder_on}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      {values.recorder_type?.toString() === CAMERA_TYPES.VISIONMAX.toString() &&
      enterpriseAccountData.recordertype.toString() ===
        CAMERA_TYPES.VISIONMAX.toString() ? (
        <AdminFormFieldAsyncLazyDropdown
          key={`recorder_id_${values.device_id}_${values.recorder_id.toString()}`} // to reset the dropdown on value change
          loadingData={
            loadingData || isLoadingCameraDevices || searchingDropdownValue
          }
          label={t("recorder_id")}
          id="recorder_id"
          name="recorder_id"
          noOptionsMessage={() => t("camera_selector_no_options")}
          placeholder={t("camera_selector_placeholder")}
          isMulti={false}
          // isSearchable={false}
          // removed `isClearable` intentionally as selecting option from the `Camera Type` dropdown clears this value
          // isClearable={true}
          defaultOption={
            !!values.recorder_id
              ? {
                  value: values.recorder_id,
                  label: values.recorder_id,
                }
              : undefined
          } // passed undefined to let placeholder display if no value is selected
          loadOptions={debouncedLoadOptionsHandlerUser}
          onChange={(e: any) => {
            if (!!e) {
              const newlySelected = e.value;
              formikSetValue("recorder_id", newlySelected);
            }
          }}
          // onChange={(e) => handleChange(e)}
          onBlur={(e) => {
            formikSetTouched("recorder_id", true);
            handleBlur(e);
          }}
          error={errors.recorder_id}
          touched={touched.recorder_id}
          additional={{ page: 0 }}
          disabled={
            !userCanEdit ||
            loadingData ||
            values.recorder_type === CAMERA_TYPES.NONE
          }
          detailsFormField={true}
          menuPlacement="top"
          value={values.recorder_id}
        />
      ) : (
        <AdminFormFieldInput
          label={t("recorder_id")}
          type="text"
          id="recorder_id"
          name="recorder_id"
          value={
            values.recorder_type.toString() === CAMERA_TYPES.NONE.toString()
              ? ""
              : values.recorder_id
          } // Show empty field if NONE is selected as camera type
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.recorder_id}
          touched={touched.recorder_id}
          disabled={
            !userCanEdit ||
            values.recorder_type.toString() === CAMERA_TYPES.NONE.toString()
          }
          detailsFormField={true}
        />
      )}

      <AdminFormFieldInput
        label={t("previous_recorder_id")}
        type="text"
        id="previous_recorder_id"
        name="previous_recorder_id"
        value={values.previous_recorder_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.previous_recorder_id}
        touched={touched.previous_recorder_id}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("recorder_id_last_changed")}
        type="date-picker"
        id="recorder_id_last_changed"
        name="recorder_id_last_changed"
        value={formattedDateTime(values.recorder_id_last_changed, "yyyy-MM-dd")}
        onDatepickerChange={(_, value) => {
          formikSetValue("recorder_id_last_changed", value);
        }}
        onBlur={(e) => {
          formikSetTouched("recorder_id_last_changed", true);
          handleBlur(e);
        }}
        error={errors.recorder_id_last_changed}
        touched={touched.recorder_id_last_changed}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
        size="lg"
      />
    </div>
  );
};

interface DeleteConfirmationModalProps {
  onConfirm: () => void;
  onCancel: () => void;
  selectedZipTie: string;
}
export const DeleteConfirmationModal = ({
  onConfirm,
  onCancel,
  selectedZipTie
}: DeleteConfirmationModalProps) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        className="w-full max-w-[420px] rounded-2xl bg-white px-5 py-7 shadow-xl"
      >
        <h2
          id="delete-modal-title"
          className="mb-3 text-lg font-bold text-gray-900"
        >
          Delete Zip Tie?
        </h2>

        <p className="mb-7 text-sm leading-5 text-gray-500">
          Are you sure you want to delete this zip tie with serial number {selectedZipTie}? This action
          cannot be undone.
        </p>

        <button
          type="button"
          onClick={onConfirm}
          className="w-full rounded-md bg-accent-blue-pale px-4 py-2 text-sm font-medium text-accent-blue-dark hover:bg-accent-blue-dark hover:text-white"
        >
          Delete
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="mt-3 w-full px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-gray-100 rounded-md"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};


interface AddZipTieModalProps {
  onConfirm: (serialNumber: string) => void;
  onCancel: () => void;
  existingValues: string[];
}
export const AddZipTieModal = ({
  onConfirm,
  onCancel,
  existingValues
}: AddZipTieModalProps) => {
  const [serialNumber, setSerialNumber] = useState("");

  const validateSerialNumber = (value: string): string => {
    if (!value.trim()) {
      return "Serial number is required.";
    }
    if (value.length > 20) {
      return "Serial number cannot exceed 20 characters.";
    }
    if (!/^[a-zA-Z0-9-]+$/.test(value)) {
      return "Only letters, numbers, and hyphens are allowed.";
    }
    const isDuplicate = existingValues.some(
      (existing) => existing.toLowerCase() === value.trim().toLowerCase()
    );
    if (isDuplicate) {
      return "This serial number already exists for this asset.";
    }
    return "";
  };
  const validationError=validateSerialNumber(serialNumber);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        className="w-full max-w-[420px] rounded-2xl bg-white px-5 py-7 shadow-xl"
      >
        <h2
          id="delete-modal-title"
          className="mb-3 text-lg font-bold text-gray-900"
        >
          Add New Zip Tie
        </h2>

        <p className="mb-5 text-sm leading-5 text-gray-500">
          Please provide the information below to add a New Zip Tie
        </p>
        <div className="mb-4">
          <AdminFormFieldInput
            label="Serial Number *"
            type="text"
            id="serial_number"
            name="serial_number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            error={validateSerialNumber(serialNumber)}
            detailsFormField={true}
            basicInputClass="h-9 w-full rounded-md text-xs text-gray-700"
            placeholder="Please provide the serial number"
          />
        </div>

        <button
          type="button"
          onClick={() => onConfirm(serialNumber.trim())}
          className="w-full rounded-md bg-accent-blue-pale px-4 py-2 text-sm font-medium text-accent-blue-dark hover:bg-accent-blue-dark hover:text-white disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:bg-gray-200 disabled:hover:text-gray-500"
          disabled={!!validationError}
        >
          Add New Zip Tie
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="mt-3 w-full px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-gray-100 rounded-md"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export interface VehicleZipTieFormHandle {
  saveChanges: () => Promise<void>;
  validateChanges: () => boolean;
}
export const VehicleZipTieForm = forwardRef<
  VehicleZipTieFormHandle,
  VehicleDetailFormProps & { device_id: string }
>(({
  userCanEdit,
  device_id,
}, ref) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.vehicles.detailsPage.form",
  });
  const { getUserACLAccessLevel } = useACL();

  const { data: vehicleZipTiesData, error: vehicleZipTiesError } = useVehicleZipTiesQuery(device_id);
  const [deleteZipTieTrigger] = useDeleteVehicleZipTieMutation();
  const [patchZipTieTrigger] = usePatchVehicleZipTieMutation();
  const [addZipTieTrigger] = usePostVehicleZipTieMutation();

  const [selectedZipTie, setSelectedZipTie] =
    useState<VehicleZipTieDetails | null>(null);
  const [editedZipTies, setEditedZipTies] = useState<{ id: number; serial_number: string }[]>([]);

  const [addZipTieModalOpen, setAddZipTieModalOpen] = useState(false);
  const { tzForDateFns } = useAccountTimezone();

  const handleDelete = async () => {
    if (!selectedZipTie) return;
    try {
      await deleteZipTieTrigger({ zip_tie_id: selectedZipTie.id }).unwrap();
    }
    catch (error) {
      console.error("Error deleting zip tie:", error);
    }
    finally {
      setSelectedZipTie(null);
    }
  };

const handleAddZipTie = async (serial_number: string) => {
  try {
    await addZipTieTrigger({ device_id, serial_number }).unwrap();
    setAddZipTieModalOpen(false);
  } catch (error) {
    console.error("Error adding zip tie:", error);
  }
};

  useImperativeHandle(ref, () => ({
     validateChanges: () => {
    const filteredEditedZipTies = editedZipTies.filter((edited) =>
      vehicleZipTiesData?.results?.some(
        (item) => item.id === edited.id
      )
    );
    return filteredEditedZipTies.every(
      (item) => !validateSerialNumber(item.serial_number, item.id)
    );
  },

    saveChanges: async () => {
      if (!editedZipTies.length) return;
      const filteredEditedZipTies = editedZipTies.filter(
        (edited) =>
          vehicleZipTiesData?.results?.some(
            (item) => item.id === edited.id
          )
      );
      const serialNumberErrors = filteredEditedZipTies.map((item) =>
        validateSerialNumber(item.serial_number, item.id)
      ).filter(Boolean);
      if (serialNumberErrors.length > 0) {
        toast.error(serialNumberErrors.join(" "));
        return;
      }

      await Promise.all(
        filteredEditedZipTies.map((item) =>
          patchZipTieTrigger({
            zip_tie_id: item.id,
            serial_number: item.serial_number,
          }).unwrap()
        )
      );
    },
  }));


  const validateSerialNumber = (
    value: string,
    currentId: string | number
  ): string => {
    const serialNumber = value.trim();

    if (!serialNumber) {
      return "Serial number is required.";
    }

    if (serialNumber.length > 20) {
      return "Serial number cannot exceed 20 characters.";
    }

    if (!/^[a-zA-Z0-9-]+$/.test(serialNumber)) {
      return "Only letters, numbers, and hyphens are allowed.";
    }

    const isDuplicate = vehicleZipTiesData?.results?.some(
      (item) =>
        item.id !== currentId &&
        item.serial_number?.trim().toLowerCase() ===
        serialNumber.toLowerCase()
    );
    if (isDuplicate) {
      return "This serial number already exists for this asset.";
    }
    return "";
  };


  if (vehicleZipTiesError && "status" in vehicleZipTiesError && vehicleZipTiesError.status === 403) {
    return (
      <div className="px-5 py-4 bg-gray-100 grid grid-cols-12 gap-4">
        <div className="col-span-12 flex justify-between items-center">
          You don't have permission to access this data.
        </div>
      </div>
    );
  }
  else
    return (
      <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
        <div className="col-span-12 flex justify-between items-center">
          <h2 className="text-lg text-gray-900">
            {t("zip_tie_form_title")}
          </h2>
          <AdminFormFieldSubmit
            label={t("zip_tie_add_button")}
            buttonDimensionClass="h-9 w-full px-4"
            type="button"
            variant="primary"
            onClick={() => {
              setAddZipTieModalOpen(true);
            }}
            disabled={!userCanEdit || (getUserACLAccessLevel(ACL_NAMES.ACL_ZIPTIE_CONFIG) < ROLE_ACCESS_LEVELS.ALL)}
          />
        </div>
        <div className="col-span-12 overflow-x-auto bg-white">
          <table className="w-full min-w-[600px] border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="px-2.5 py-3 text-sm font-semibold text-sky-700">
                  Serial Number
                </th>
                <th className="px-2.5 py-3 text-sm font-semibold text-sky-700">
                  Added Date
                </th>
                <th className="px-2.5 py-3 text-sm font-semibold text-sky-700">
                  Added By
                </th>
                <th className="px-2.5 py-3 text-center text-sm font-semibold text-sky-700">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {vehicleZipTiesData?.results.map((item: VehicleZipTieDetails) => {
                const serialNumber =
                  editedZipTies.find((zipTie) => zipTie.id === item.id)?.serial_number
                  ?? item.serial_number;
                return (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                  >
                    <td className="px-2.5 py-3">
                      <AdminFormFieldInput
                        label=""
                        type="text"
                        id={`serial_number_${item.id}`}
                        name="serial_number"
                        value={serialNumber}
                        onChange={(e) => {
                          const serialNumber = e.target.value;
                          setEditedZipTies((prev) => {
                            const existing = prev.some((zipTie) => zipTie.id === item.id);
                            if (existing) {
                              return prev.map((zipTie) =>
                                zipTie.id === item.id
                                  ? { ...zipTie, serial_number: serialNumber }
                                  : zipTie
                              );
                            }
                            return [...prev, { id: item.id, serial_number: serialNumber },];
                          });
                        }}
                        error={validateSerialNumber(serialNumber, item.id)}
                        detailsFormField={true}
                        basicInputClass="h-9 rounded-md text-xs bg-grey-100"
                        readOnly={!userCanEdit || (getUserACLAccessLevel(ACL_NAMES.ACL_ZIPTIE_CONFIG) < ROLE_ACCESS_LEVELS.WRITE)}
                      />
                    </td>

                    <td className="px-2.5 py-3 text-xs text-gray-500">
                      {formattedDateTimeBasedOnTimezone(item.created_at, tzForDateFns)}
                    </td>

                    <td className="px-2.5 py-3 text-xs text-gray-500">
                      {item.created_by}
                    </td>

                    <td className="px-2.5 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => setSelectedZipTie(item)}
                        aria-label={`Delete serial number ${item.serial_number}`}
                        title="Delete"
                        className="inline-flex items-center justify-center text-gray-400 hover:text-red-600 disabled:cursor-not-allowed"
                        disabled={!userCanEdit || (getUserACLAccessLevel(ACL_NAMES.ACL_ZIPTIE_CONFIG) < ROLE_ACCESS_LEVELS.ALL)}
                      >
                        <img className="w-4 h-4" src={svgIcons.Delete} alt="Delete Icon" />
                      </button>
                    </td>
                  </tr>
                )
              })}

              {vehicleZipTiesData?.count === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-center text-xs text-gray-500"
                  >
                    No zip ties found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {selectedZipTie && (
          <DeleteConfirmationModal
            onConfirm={handleDelete}
            onCancel={() => setSelectedZipTie(null)}
            selectedZipTie={selectedZipTie.serial_number}
          />
        )}
        {addZipTieModalOpen && (
          <AddZipTieModal
            onConfirm={(serial_number) => handleAddZipTie(serial_number)}
            onCancel={() => setAddZipTieModalOpen(false)}
            existingValues={vehicleZipTiesData?.results?.map((item) => item.serial_number) ?? []}
          />
        )}
      </div>
    )
}
);

export const VehicleGroupMembershipForm: FC<VehicleDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.vehicles.detailsPage.form",
  });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  // prepare group query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgGroupsQueryParams, setOrgGroupsQueryParams] = useState({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
  });

  // fetch organization groups
  const { data: initialGroups, isFetching: isFetchingInitialGroups } =
    useOrganizationGroupsQuery(orgGroupsQueryParams);

  // fetch searched groups
  const [orgGroupsApiTrigger, { isFetching: isFetchingGroups }] =
    useLazyOrganizationGroupsQuery();

  const loadOptionsHandlerGroups = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const newOrgGroupsQueryParams = {
      ...orgGroupsQueryParams,
      page: additional?.page ?? 2,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.GROUP_DROPDOWN.join(","), // search by groupid only
    };
    setOrgGroupsQueryParams(newOrgGroupsQueryParams);
    const { data: groupsResponse } = await orgGroupsApiTrigger(
      newOrgGroupsQueryParams
    );

    if (APP_CONFIG.DEBUG.ALL) console.log("groupsResponse", groupsResponse);

    if (!groupsResponse || !groupsResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }
    const { results } = groupsResponse as { results: TLegacyGroup[] };
    return {
      options: !!results
        ? (results || ([] as TLegacyGroup[]))
            ?.filter(
              (item) =>
                !(values.selected_groups as string[]).includes(
                  `${item.groupid}`
                )
            )
            .map(
              (item: TLegacyGroup, index: number) =>
                ({
                  value: `${item.groupid}`,
                  label: item.displayname || item.groupid,
                }) as TSelectboxOption
            )
        : [],
      hasMore: groupsResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerGroups = asyncDebounce(
    loadOptionsHandlerGroups,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const handleGroupChange = async (e: any) => {
    console.log(e, "selected group");
    const newlySelectedGroup = e;
    if (!!e) {
      formikSetValue("selected_groups", [...newlySelectedGroup]);
    }
  };

  // add all groups to vehicle
  const addAllGroups = () => {
    formikSetValue("selected_groups", []);
  };

  // handle group removal
  const onRemoveFromGroup = (option: any) => {
    formikSetValue(
      "selected_groups",
      (values.selected_groups as TSelectboxOption[]).filter(
        (selectedGroup: any) => selectedGroup.value !== option
      )
    );
  };

  const MemoizedFormActionSelectGroupsButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: tMain("select_groups_button"),
              type: "button",
              variant: "primary-like",
              customWrapperClass: "col-span-3",
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showHierarchyGroupSelector: true,
                  })
                );
              },
              disabled: !userCanEdit || loadingData || !!values.all_groups, // disable if user cannot edit or all groups is selected
            },
          ]}
        />
      </>
    );
  }, [userCanEdit, loadingData, values.all_groups, modalsState]);

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          label={t("all_groups")}
          id="all_groups"
          type="checkbox"
          name="all_groups"
          checked={!!values.all_groups}
          onChange={(e) => {
            handleChange?.(e);
            addAllGroups();
            setTimeout(() => {
              formikSetTouched("selected_groups");
            }, 100);
          }}
          onBlur={handleBlur}
          error={errors.all_groups}
          touched={touched.all_groups}
          disabled={!userCanEdit}
        />
      </div>
      {errors.selected_groups && userCanEdit && (
        <span className="col-span-12 bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs">
          {errors.selected_groups}
        </span>
      )}
      {loadingData ? (
        <PseudoSelect label={t("group_selector")} />
      ) : (
        <>
          {accountHasGroupHierarchyEnabled === "1" ? (
            <>
              {/* <AdminFormFieldSubmit
                  label={tMain('select_groups_button')}
                  type="button"
                  variant="primary-like"
                  customWrapperClass="col-span-3"
                  onClick={() => { dispatch(setModalsData({ ...modalsState, showHierarchyGroupSelector: true, })) }}
                  disabled={!userCanEdit || loadingData || !!values.all_groups} // disable if user cannot edit or all groups is selected
                /> */}
              {MemoizedFormActionSelectGroupsButton}
              {modalsState.showHierarchyGroupSelector && (
                <HierarchyGroupSelector
                  initiallySelectedGroups={values.selected_groups.map(
                    (item: any) => item.value
                  )}
                  onSelectionChange={(selectedGroups: string[]) => {
                    if (APP_CONFIG.DEBUG.DISPLAY_VERBOSE)
                      console.log("VALUES RECEIVED:", selectedGroups);
                    formikSetValue(
                      "selected_groups",
                      selectedGroups?.map((item) => ({
                        label: `${item}`,
                        value: `${item}`,
                      })) || []
                    );
                    setTimeout(() => {
                      formikSetTouched("selected_groups");
                    }, 100); // to trigger validation
                  }}
                />
              )}
            </>
          ) : (
            <AdminFormFieldAsyncLazyDropdown
              key={`group_selector_`} // to reset the dropdown on group selection
              loadingData={
                loadingData || isFetchingInitialGroups || isFetchingGroups
              }
              label={t("group_selector")}
              id="group_selector"
              name="group_selector"
              noOptionsMessage={() => t("group_selector_no_options")}
              // defaultOptions={initialGroups?.results
              //   ?.filter(
              //     (item) => !values.selected_groups.includes(`${item.groupid}`)
              //   )
              //   ?.map((item: TLegacyGroup) => ({
              //     value: `${item.groupid}`,
              //     label: item.displayname || item.groupid,
              //   }))}
              placeholder={t("group_selector_placeholder")}
              isMulti={true}
              customWrapperClass="col-span-12"
              loadOptions={debouncedLoadOptionsHandlerGroups}
              onChange={handleGroupChange}
              onBlur={(e) => {
                formikSetTouched("selected_groups", true);
                handleBlur(e);
              }}
              additional={{ page: 1 }}
              error={errors.group_selector}
              touched={touched.group_selector}
              disabled={!userCanEdit || loadingData || values.all_groups}
              detailsFormField={true}
              menuPlacement="top"
              value={values.selected_groups as TSelectboxOption[]}
            />
          )}
        </>
      )}
      {accountHasGroupHierarchyEnabled === "1" && (
        <div className="col-span-12 grid grid-cols-12">
          <AdminFormFieldPillList
            disabled={!userCanEdit || values.all_groups}
            isError={errors.selected_groups && userCanEdit}
          >
            {values.selected_groups.map(
              (group: string | { label: string; value: string }, i: number) => (
                <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                  key={`pillItem_${i}`}
                  disabled={!userCanEdit || values.all_groups}
                  item={
                    {
                      name: typeof group === "string" ? group : group.label,
                    } as PillItem
                  }
                  onRemove={() =>
                    onRemoveFromGroup(
                      typeof group === "string" ? group : group.value
                    )
                  }
                  onItemClick={(item) => {
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        showReadOnlyViewForHierarchyGroup: {
                          accountid: thisUserOrganizationId,
                          active_inactive_vehicle_counts: {
                            active_vehicle_count: 0,
                            inactive_vehicle_count: 0,
                          },
                          description: "",
                          groupid:
                            typeof group === "string" ? group : group.value,
                        } as OrgGHGroup,
                        showHierarchyGroupSelector: true,
                      })
                    );
                  }}
                />
              )
            )}
          </AdminFormFieldPillList>
        </div>
      )}
    </div>
  );
};
